const versionPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;

export function parseVersion(version: string): { numbers: number[]; prerelease: string[] } | null {
  const match = versionPattern.exec(version);
  if (!match) return null;

  const numbers = match.slice(1, 4).map(Number);
  if (numbers.some((part) => !Number.isSafeInteger(part))) return null;
  if (match[4]?.split('.').some(part => /^0\d+$/.test(part))) return null;

  return { numbers, prerelease: match[4]?.split('.') ?? [] };
}

/** Positive when left is newer than right. Null means an invalid version. */
export function compareVersions(left: string, right: string): number | null {
  const a = parseVersion(left);
  const b = parseVersion(right);
  if (!a || !b) return null;

  for (let index = 0; index < 3; index++) {
    if (a.numbers[index] !== b.numbers[index]) {
      return a.numbers[index] > b.numbers[index] ? 1 : -1;
    }
  }

  if (!a.prerelease.length && !b.prerelease.length) return 0;
  if (!a.prerelease.length) return 1;
  if (!b.prerelease.length) return -1;

  for (let index = 0; index < Math.max(a.prerelease.length, b.prerelease.length); index++) {
    const leftPart = a.prerelease[index];
    const rightPart = b.prerelease[index];
    if (leftPart === undefined) return -1;
    if (rightPart === undefined) return 1;
    if (leftPart === rightPart) continue;

    const leftNumeric = /^\d+$/.test(leftPart);
    const rightNumeric = /^\d+$/.test(rightPart);
    if (leftNumeric && rightNumeric) {
      const normalizedLeft = leftPart.replace(/^0+(?=\d)/, '');
      const normalizedRight = rightPart.replace(/^0+(?=\d)/, '');
      if (normalizedLeft.length !== normalizedRight.length) {
        return normalizedLeft.length > normalizedRight.length ? 1 : -1;
      }
      if (normalizedLeft !== normalizedRight) return normalizedLeft > normalizedRight ? 1 : -1;
    } else if (leftNumeric !== rightNumeric) {
      return leftNumeric ? -1 : 1;
    } else {
      return leftPart < rightPart ? -1 : 1;
    }
  }

  return 0;
}

export function isNewerOtaVersion(available: string, current: string | null): boolean {
  if (!parseVersion(available)) return false;
  if (current === null) return true;
  return (compareVersions(available, current) ?? -1) > 0;
}
